import type { ClustererObject, YMapClustererProps } from '@yandex/ymaps3-clusterer'
import type { LngLat, YMapCollection, YMapListener, Feature, Projection, GenericPointGeometry, YMapMarker } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'

import type { ClusterFeatureEntity } from '@/modules/DispatcherWorkplaceModule/types'

export type CustomClusterProps<T> = YMapClustererProps & {
    clusterPopup?: (coordinates: LngLat, features: Array<ClusterFeature<T>>, close: () => void) => HTMLElement
    markerPopup?: (feature: ClusterFeature<T>, close: () => void) => HTMLElement
}

function throttle<T extends (...args: any[]) => void>(func: T, timeout: number): T {
    let lastExecTime = 0
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    return function (this: any, ...args: Parameters<T>) {
        const now = Date.now()

        if (lastExecTime && now < lastExecTime + timeout) {
            if (timeoutId) clearTimeout(timeoutId)
            timeoutId = setTimeout(() => {
                lastExecTime = now
                func.apply(this, args)
            }, timeout - (now - lastExecTime))
        } else {
            lastExecTime = now
            func.apply(this, args)
        }
    } as T
}

class PopupController {
    private static _current: {
        clusterId: string,
        clusterer: ReturnType<typeof CustomClusterer>
    } | null = null;


    public static register(clusterId: string, clusterer: ReturnType<typeof CustomClusterer>) {
        if (this._current && this._current.clusterId !== clusterId) {
            this._current.clusterer.closePopup()
        }

        this._current = { clusterId, clusterer }
    }

    public static clear() {
        this._current = null
    }

    public static forceClose() {
        if (this._current) {
            this._current.clusterer.closePopup()
        }

        this.clear()
    }
}

export function CustomClusterer<T>(props: CustomClusterProps<T>) {
    const { YMapComplexEntity, YMapMarker } = ymaps3
    
    class CustomClusterer<T> extends YMapComplexEntity<CustomClusterProps<T>> {
        private _entitiesCache: Record<string, any> = {}
        private _visibleEntities: Record<string, any> = {}
        private _container: YMapCollection
        private _mapListener: YMapListener
        private _activePopup: YMapMarker | null = null

        private _clusters: ClustererObject[] = []
        private _updateIntervalForPopup: ReturnType<typeof setInterval> | null = null

        constructor(props: YMapClustererProps) {
            super(props)
            this._render = this._render.bind(this)
        }

        private _isVisible(feature: Feature, bounds: LngLat[], projection: Projection): boolean {
            const { x, y } = projection.toWorldCoordinates((feature.geometry as GenericPointGeometry<LngLat>).coordinates)
            const { x: x1, y: y1 } = projection.toWorldCoordinates(bounds[0])
            const { x: x2, y: y2 } = projection.toWorldCoordinates(bounds[1])

            const width = x2 - x1
            const height = y1 - y2

            return x1 - width / 2 <= x && x2 + width / 2 >= x && y1 + height / 2 >= y && y2 - height / 2 <= y
        }

        private _getEntity({ lnglat, features, clusterId }: ClustererObject) {
            if (this._entitiesCache[clusterId]) {
                return this._entitiesCache[clusterId]
            }

            const entity = features.length === 1
                ? this._props.marker(features[0])
                : this._props.cluster(lnglat, features)

            entity.update({
                onFastClick: () => this._togglePopup(clusterId, lnglat, features as Array<ClusterFeature<T>>, false)
            })
    
            this._entitiesCache[clusterId] = entity
            return entity
        }

        private _getVisibleEntities(clusters: ClustererObject[]) {
            const visibleEntities: Record<string, any> = {}

            clusters.forEach(cluster => {
                visibleEntities[cluster.clusterId] = this._getEntity(cluster)
            })
            
            return visibleEntities
        }

        private _syncVisibleEntities(visibleEntities: Record<string, any>) {
            let popupStillVisible = false

            for (const clusterId in this._visibleEntities) {
                if (!visibleEntities[clusterId]) {
                    this._container.removeChild(this._visibleEntities[clusterId])
                } else if (this._activePopup && ((this._activePopup as any)._props)) {
                    const popupId = String((this._activePopup as any)._props.id).trim()
                    const entityId = String(clusterId).trim()

                    if (popupId === entityId) {
                        popupStillVisible = true
                    }
                }
            }
    
            for (const clusterId in visibleEntities) {
                if (!this._visibleEntities[clusterId]) {
                    this._container.addChild(visibleEntities[clusterId])
                }
            }
    
            this._visibleEntities = visibleEntities
            
            if (!popupStillVisible) {
                this._closePopup()
            }
        }

        private _render({ mapInAction = false } = {}) {
            if (!this.root || mapInAction) return
    
            const map = this.root

            const visibleFeatures = this._props.features.filter(feature =>
                this._isVisible(feature as ClusterFeature<T>, map.bounds, map.projection)
            )
    
            let clusters: ClustererObject[] = []
            
            if (this._props.maxZoom && map.zoom > this._props.maxZoom) {
                clusters = visibleFeatures.map(feature => {
                    return {
                        world: map.projection.toWorldCoordinates(feature.geometry.coordinates),
                        lnglat: feature.geometry.coordinates,
                        clusterId: feature.id,
                        features: [feature]
                    }
                })
            } else {
                clusters = this._props.method.render({ map, features: visibleFeatures })
            }

            this._clusters = clusters
    
            if (this._props.onRender && this._props.onRender(clusters) === false) {
                return
            }
    
            const visibleEntities = this._getVisibleEntities(clusters)
            this._syncVisibleEntities(visibleEntities)
        }

        protected _onAttach() {
            this._entitiesCache = {}
            this._visibleEntities = {}
    
            this._container = new ymaps3.YMapCollection({})
            this.addChild(this._container)
    
            const throttledRender = throttle(this._render, 200)
    
            this._mapListener = new ymaps3.YMapListener({
                onUpdate: throttledRender,
                onResize: throttledRender
            })
    
            this.addChild(this._mapListener)
            this._render()
        }

        protected _onDetach() {
            this.removeChild(this._container)
            this.removeChild(this._mapListener)
            this._entitiesCache = {}
            this._visibleEntities = {}
        }
    
        protected _onUpdate() {
            this._render()
        }

        private _togglePopup(clusterId: string, coordinates: LngLat, features: Array<ClusterFeature<T>>, updateLocation: boolean) {
            PopupController.forceClose()
            this._closePopup()

            PopupController.register(clusterId, this)
            this._openPopup(clusterId, coordinates, features)

            if (this.root) {
                if (updateLocation) {
                    this.root.setLocation({ center: coordinates, duration: 500, easing: 'ease-in' })
                }
            }
        }

        private _handlePopupWheel = (event: Event): void => {
            event.stopPropagation()
        }

        private _onPopupMouseEnter = (): void => {
            if (this._activePopup) {
                this._activePopup.element.addEventListener('wheel', this._handlePopupWheel)
            }
        }

        private _onPopupMouseLeave = (): void => {
            if (this._activePopup) {
                this._activePopup.element.removeEventListener('wheel', this._handlePopupWheel)
            }
        }

        private _openPopup(clusterId: string, coordinates: LngLat, features: Array<ClusterFeature<T>>) {
            if (features.length === 1) {
                if (!this._props.markerPopup) return
                const popupContent = this._props.markerPopup(features[0], () => this._closePopup())

                this._activePopup = new YMapMarker({ id: clusterId, coordinates, zIndex: 1000 }, popupContent)
                this._activePopup.element.style.display = 'block'
                this._activePopup.element.style.width = '0'
                this._activePopup.element.style.height = '0'
                this._activePopup.element.classList.add('dw-popup-animation')

                this._container.addChild(this._activePopup)
            } else {
                if (!this._props.clusterPopup) return

                const popupContent = this._props.clusterPopup(
                    coordinates,
                    features,
                    () => this._closePopup(),
                )

                this._activePopup = new YMapMarker({ id: clusterId, coordinates, zIndex: 1000 }, popupContent)
                this._activePopup.element.style.display = 'block'
                this._activePopup.element.style.width = '0'
                this._activePopup.element.style.height = '0'
                this._activePopup.element.classList.add('dw-popup-animation')
                this._activePopup.element.setAttribute('data-cluster-popup', 'true')

                this._activePopup.element.addEventListener('mouseenter', this._onPopupMouseEnter)
                this._activePopup.element.addEventListener('mouseleave', this._onPopupMouseLeave)
                
                this._container.addChild(this._activePopup)
            }
        }

        private _closePopup() {
            if (this._activePopup) {
                this._activePopup.element.style.display = 'none'
                this._activePopup.element.removeEventListener('mouseenter', this._onPopupMouseEnter)
                this._activePopup.element.removeEventListener('mouseleave', this._onPopupMouseLeave)

                this._onPopupMouseLeave()

                this._container.removeChild(this._activePopup)
                this._activePopup = null
            }
        }

        private _waitThenClusterWillFinded(featureId: string) {
            const neededCluster = this._clusters.find((cluster) => cluster.features.some((feature) => feature.id === featureId))

            if (neededCluster) {
                this._togglePopup(neededCluster.clusterId, neededCluster.lnglat, neededCluster.features as Array<ClusterFeature<T>>, false)
                clearInterval(this._updateIntervalForPopup as ReturnType<typeof setInterval>)
            }
        }

        private _clearPopupInterval() {
            if (this._updateIntervalForPopup) {
                clearInterval(this._updateIntervalForPopup)
            }
        }

        public closePopup() {
            this._clearPopupInterval()
            this._closePopup()
        }

        public openPopup(featureId: string) {
            if (!this.root) return

            const neededCluster = this._clusters.find((cluster) => cluster.features.some((feature) => feature.id === featureId))

            if (neededCluster) {
                this._togglePopup(neededCluster.clusterId, neededCluster.lnglat, neededCluster.features as Array<ClusterFeature<T>>, false)
            } else {
                this._updateIntervalForPopup = setInterval(() => {
                    this._waitThenClusterWillFinded(featureId)
                }, 500)
            }
        }
    }

    return new CustomClusterer(props)
}